import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const departmentRoles = [
  {
    department: "Administration",
    roles: [
      {
        title: "Utility Administrator",
        summary: "Configures system settings, manages master data, sets up workflows, maintains user roles, and develops reports for leadership."
      },
      {
        title: "Utility Manager",
        summary: "Has oversight access across all departments to ensure operations are running efficiently. Responsible for monitoring cross-departmental processes, identifying bottlenecks, and implementing improvements organization-wide."
      }
    ]
  },
  {
    department: "Customer Service",
    roles: [
      {
        title: "CSO Manager",
        summary: "Oversees all customer touchpoints, implements service strategies, analyzes customer data, and manages service metrics."
      },
      {
        title: "Customer Executive",
        summary: "Handles walk-in customers, processes account changes, resolves billing disputes, and manages complex in-person transactions."
      },
      {
        title: "Call Center Representative",
        summary: "Manages customer calls, creates service orders, answers billing questions, and schedules field service appointments."
      },
      {
        title: "Recovery Executive",
        summary: "Manages delinquent accounts, negotiates payment arrangements, processes disconnections, and tracks payment compliance."
      }
    ]
  },
  {
    department: "Metering",
    roles: [
      {
        title: "Meter Manager",
        summary: "Oversees metering operations, manages reading schedules, ensures data quality, and implements metering technologies."
      },
      {
        title: "Meter Supervisor",
        summary: "Manages meter inventory, oversees testing and replacement programs, and ensures meter compliance with standards."
      },
      {
        title: "Meter Reading Supervisor",
        summary: "Manages reading operations, optimizes routes, assigns work, and addresses reading exceptions."
      },
      {
        title: "Meter Reader",
        summary: "Collects consumption data, identifies access issues, reports meter problems, and performs basic maintenance."
      },
      {
        title: "Validator",
        summary: "Reviews reading exceptions, analyzes consumption patterns, makes estimation decisions, and approves readings for billing."
      }
    ]
  },
  {
    department: "Billing",
    roles: [
      {
        title: "Billing Manager",
        summary: "Oversees billing cycles, ensures accurate rate implementation, manages exceptions, and coordinates bill delivery."
      },
      {
        title: "Billing Specialist",
        summary: "Executes billing cycles, resolves exceptions, processes adjustments, and implements rate changes."
      },
      {
        title: "Printing Vendor",
        summary: "Processes billing files, produces physical bills, ensures timely delivery, and reports on production metrics."
      }
    ]
  },
  {
    department: "Operations & Maintenance",
    roles: [
      {
        title: "O&M Manager",
        summary: "Oversees system maintenance, manages emergency response, ensures regulatory compliance, and allocates operational resources."
      },
      {
        title: "Field Force Technician",
        summary: "Performs maintenance and repairs, responds to service requests, and documents field activities."
      },
      {
        title: "Dispatcher",
        summary: "Assigns and schedules work orders, tracks field crews, prioritizes emergencies, and coordinates resources."
      }
    ]
  },
  {
    department: "Asset Management",
    roles: [
      {
        title: "Asset Manager",
        summary: "Develops asset strategies, prioritizes investments, tracks infrastructure performance, and plans renewal programs."
      },
      {
        title: "Plant Supervisor",
        summary: "Manages treatment facilities, oversees processes, ensures water quality compliance, and coordinates maintenance."
      },
      {
        title: "Assets Technician",
        summary: "Conducts field assessments, updates asset records, collects performance data, and develops maintenance recommendations."
      }
    ]
  },
  {
    department: "IT",
    roles: [
      {
        title: "IT Manager",
        summary: "Oversees IT systems and infrastructure, manages IT staff, and ensures system security and reliability."
      },
      {
        title: "System Administrator",
        summary: "Maintains servers, networks, and IT infrastructure to ensure optimal performance and security."
      }
    ]
  },
  {
    department: "HR",
    roles: [
      {
        title: "HR Manager",
        summary: "Oversees all HR operations including recruitment, training, benefits, and employee relations."
      },
      {
        title: "HR Specialist",
        summary: "Handles day-to-day HR tasks including onboarding, benefits administration, and employee inquiries."
      }
    ]
  },
  {
    department: "Finance",
    roles: [
      {
        title: "Finance Manager",
        summary: "Oversees financial operations, budgeting, forecasting, and financial reporting."
      },
      {
        title: "Accountant",
        summary: "Manages accounting records, processes transactions, and prepares financial statements."
      }
    ]
  },
  {
    department: "Operations",
    roles: [
      {
        title: "Operations Manager",
        summary: "Oversees daily operations, optimizes processes, and ensures operational efficiency."
      },
      {
        title: "Operations Analyst",
        summary: "Analyzes operational data, identifies improvement opportunities, and supports operational decision-making."
      }
    ]
  },
  {
    department: "Sales",
    roles: [
      {
        title: "Sales Manager",
        summary: "Oversees sales team, develops sales strategies, and meets revenue targets."
      },
      {
        title: "Sales Representative",
        summary: "Builds client relationships, presents solutions, and closes sales opportunities."
      }
    ]
  },
  {
    department: "Marketing",
    roles: [
      {
        title: "Marketing Manager",
        summary: "Develops marketing strategies, oversees campaigns, and analyzes market trends."
      },
      {
        title: "Marketing Specialist",
        summary: "Executes marketing initiatives, manages social media, and creates marketing content."
      }
    ]
  },
  {
    department: "Executive",
    roles: [
      {
        title: "CEO",
        summary: "Provides overall leadership and strategic direction for the organization."
      },
      {
        title: "CFO",
        summary: "Oversees financial operations, risk management, and financial strategy."
      },
      {
        title: "CTO",
        summary: "Directs technology strategy, innovation initiatives, and technical operations."
      }
    ]
  }
];
type RoleVisibility = {
  [department: string]: {
    enabled: boolean;
    roles: {
      [role: string]: boolean;
    };
  };
};

interface RoleVisibilityState {
  visibilitySettings: RoleVisibility;
  toggleDepartment: (department: string, enabled: boolean) => void;
  toggleRole: (department: string, role: string, enabled: boolean) => void;
  isDepartmentEnabled: (department: string) => boolean;
  isRoleEnabled: (department: string, role: string) => boolean;
  getEnabledDepartments: () => string[];
  getEnabledRolesForDepartment: (department: string) => string[];
}

// Initialize the state with all departments and roles enabled by default
const initializeRoleVisibility = (): RoleVisibility => {
  const visibility: RoleVisibility = {};
  
  departmentRoles.forEach(dept => {
    visibility[dept.department] = {
      enabled: true,
      roles: {}
    };
    
    dept.roles.forEach(role => {
      visibility[dept.department].roles[role.title] = true;
    });
  });
  
  return visibility;
};

export const useRoleVisibilityStore = create<RoleVisibilityState>()(
  persist(
    (set, get) => ({
      visibilitySettings: initializeRoleVisibility(),
      
      toggleDepartment: (department, enabled) => 
        set(state => {
          const newSettings = { ...state.visibilitySettings };
          
          if (newSettings[department]) {
            newSettings[department].enabled = enabled;
            
            // If department is disabled, also disable all its roles
            if (!enabled) {
              Object.keys(newSettings[department].roles).forEach(role => {
                newSettings[department].roles[role] = false;
              });
            } else {
              // If department is enabled, enable all its roles by default
              Object.keys(newSettings[department].roles).forEach(role => {
                newSettings[department].roles[role] = true;
              });
            }
          }
          
          return { visibilitySettings: newSettings };
        }),
      
      toggleRole: (department, role, enabled) =>
        set(state => {
          const newSettings = { ...state.visibilitySettings };
          
          if (newSettings[department] && newSettings[department].roles[role] !== undefined) {
            newSettings[department].roles[role] = enabled;
            
            // If enabling a role, make sure the department is enabled too
            if (enabled && !newSettings[department].enabled) {
              newSettings[department].enabled = true;
            }
            
            // If all roles are disabled, disable the department too
            if (!enabled && Object.values(newSettings[department].roles).every(status => !status)) {
              newSettings[department].enabled = false;
            }
          }
          
          return { visibilitySettings: newSettings };
        }),
      
      isDepartmentEnabled: (department) => {
        return get().visibilitySettings[department]?.enabled ?? false;
      },
      
      isRoleEnabled: (department, role) => {
        return get().visibilitySettings[department]?.roles[role] ?? false;
      },
      
      getEnabledDepartments: () => {
        const { visibilitySettings } = get();
        return Object.keys(visibilitySettings).filter(
          dept => visibilitySettings[dept].enabled
        );
      },
      
      getEnabledRolesForDepartment: (department) => {
        const { visibilitySettings } = get();
        if (!visibilitySettings[department]?.enabled) return [];
        
        return Object.keys(visibilitySettings[department].roles).filter(
          role => visibilitySettings[department].roles[role]
        );
      }
    }),
    {
      name: 'role-visibility-settings',
    }
  )
);